import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription 
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { 
  Search, 
  FileText,
  Loader2,
  Download,
  Trash2,
  Calendar,
  BarChart3
} from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';
import { API_URL } from '../config/api';

interface ResearchTopic {
  id: number;
  topic_name: string;
  status: string;
  sector_name: string;
  has_research: boolean;
  research_created_at?: string;
  research_quality_score?: number;
  research_length?: number;
}

interface ResearchDocument {
  id: number;
  topic_id: number;
  topic_name: string;
  topic_description: string;
  sector_name: string;
  research_content: string;
  research_summary?: string;
  key_findings?: any;
  sources?: any;
  research_quality_score?: number;
  created_at: string;
}

export default function ResearchDocuments() {
  const [topics, setTopics] = useState<ResearchTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedResearch, setSelectedResearch] = useState<{ open: boolean; topicId: number | null; research: ResearchDocument | null }>({ open: false, topicId: null, research: null });
  const [loadingResearch, setLoadingResearch] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch topics with research status
  const fetchTopics = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/topics/research-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (response.status === 401) {
        // Token is invalid or expired, redirect to login
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch topics');
      }
      
      const data = await response.json();
      console.log('Research status API response:', data);
      setTopics(data.topics || []);
    } catch (error) {
      console.error('Error fetching topics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load research documents',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopics();
  }, []);

  // View research document
  const handleViewResearch = async (topicId: number) => {
    try {
      setLoadingResearch(true);
      setSelectedResearch({ open: true, topicId, research: null });
      
      const response = await fetch(`${API_URL}/topics/${topicId}/research`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (response.status === 401) {
        // Token is invalid or expired, redirect to login
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch research');
      }
      
      const data = await response.json();
      console.log('Topic research API response:', data);
      // The API returns an array of research documents, get the first one
      const research = Array.isArray(data) ? data[0] : data.research || data;

      // DEBUG: Check content format
      if (research?.research_content) {
        console.log('=== RESEARCH CONTENT DEBUG ===');
        console.log('Content length:', research.research_content.length);
        console.log('First 300 chars:', research.research_content.substring(0, 300));
        console.log('Has \\n:', research.research_content.includes('\n'));
        console.log('Count of \\n:', (research.research_content.match(/\n/g) || []).length);
        console.log('Has double space:', research.research_content.includes('  '));
        console.log('==============================');
      }

      setSelectedResearch({ open: true, topicId, research });
    } catch (error) {
      console.error('Error fetching research:', error);
      toast({
        title: 'Error',
        description: 'Failed to load research document',
        variant: 'destructive',
      });
      setSelectedResearch({ open: false, topicId: null, research: null });
    } finally {
      setLoadingResearch(false);
    }
  };

  // Delete research document
  const handleDeleteResearch = async () => {
    if (!selectedResearch.topicId) return;
    
    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/topics/${selectedResearch.topicId}/research`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (response.status === 401) {
        // Token is invalid or expired, redirect to login
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to delete research');
      }
      
      toast({
        title: 'Success',
        description: 'Research document deleted successfully',
      });
      
      setSelectedResearch({ open: false, topicId: null, research: null });
      fetchTopics();
    } catch (error) {
      console.error('Error deleting research:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete research document',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Filter topics
  const filteredTopics = topics.filter(topic => 
    topic.has_research && (
      topic.topic_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      topic.sector_name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const topicsWithResearch = topics.filter(t => t.has_research).length;
  const avgQualityScore = topics
    .filter(t => t.has_research && t.research_quality_score)
    .reduce((acc, t) => acc + (t.research_quality_score || 0), 0) / (topicsWithResearch || 1);

  const getQualityBadge = (score?: number) => {
    if (!score) return null;
    const variant = score >= 0.7 ? 'default' : score >= 0.4 ? 'secondary' : 'outline';
    return (
      <Badge variant={variant}>
        {(score * 100).toFixed(0)}%
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Research Documents</h1>
          <p className="text-muted-foreground">
            View and manage AI-generated research documents for topics
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Documents</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{topicsWithResearch}</div>
            <p className="text-xs text-muted-foreground">
              Research documents generated
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Research</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {topics.filter(t => !t.has_research && t.status === 'approved').length}
            </div>
            <p className="text-xs text-muted-foreground">
              Approved topics awaiting research
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Quality Score</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {avgQualityScore > 0 ? `${(avgQualityScore * 100).toFixed(0)}%` : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              Average research quality
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Research Documents Table */}
      <Card>
        <CardHeader>
          <CardTitle>Research Documents</CardTitle>
          <CardDescription>
            All topics with generated research content
          </CardDescription>
          <div className="flex items-center gap-4 pt-4">
            <div className="flex items-center space-x-2 flex-1">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search topics or sectors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-sm"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Topic</TableHead>
                  <TableHead>Sector</TableHead>
                  <TableHead>Research Date</TableHead>
                  <TableHead>Quality Score</TableHead>
                  <TableHead>Content Size</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTopics.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">
                      No research documents found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTopics.map((topic) => (
                    <TableRow key={topic.id}>
                      <TableCell>
                        <div className="font-medium">{topic.topic_name}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {topic.sector_name}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {topic.research_created_at 
                          ? new Date(topic.research_created_at).toLocaleDateString()
                          : 'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        {getQualityBadge(topic.research_quality_score)}
                      </TableCell>
                      <TableCell>
                        {topic.research_length 
                          ? `${(topic.research_length / 1024).toFixed(1)} KB`
                          : 'N/A'
                        }
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewResearch(topic.id)}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Research Document Dialog */}
      <Dialog open={selectedResearch.open} onOpenChange={(open) => !open && setSelectedResearch({ open: false, topicId: null, research: null })}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Research Document</DialogTitle>
            <DialogDescription>
              {selectedResearch.research?.topic_name || 'Loading...'}
            </DialogDescription>
          </DialogHeader>

          {loadingResearch ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : selectedResearch.research ? (
            <ScrollArea className="h-[calc(85vh-12rem)] pr-4">
              <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label>Sector</Label>
                  <p className="font-medium">{selectedResearch.research.sector_name}</p>
                </div>
                <div>
                  <Label>Research Date</Label>
                  <p className="font-medium">
                    {new Date(selectedResearch.research.created_at).toLocaleString()}
                  </p>
                </div>
                {selectedResearch.research.research_quality_score && (
                  <div>
                    <Label>Quality Score</Label>
                    <p className="font-medium">
                      {(selectedResearch.research.research_quality_score * 100).toFixed(0)}%
                    </p>
                  </div>
                )}
              </div>
              
              {selectedResearch.research.research_summary && (
                <div>
                  <Label>Summary</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedResearch.research.research_summary}
                  </p>
                </div>
              )}
              
              <div>
                <Label>Research Content</Label>
                <div className="mt-2 border rounded-md p-4 bg-white dark:bg-slate-800 dark:border-slate-700">
                  <div className="prose prose-sm max-w-none prose-headings:mt-6 prose-headings:mb-3 prose-headings:font-bold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:mb-4 prose-p:leading-7 prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6 prose-li:mb-2 prose-strong:font-semibold prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:underline hover:prose-a:text-blue-800 dark:hover:prose-a:text-blue-300">
                    <div
                      className="text-gray-700 dark:text-gray-300 leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: (() => {
                          let content = selectedResearch.research.research_content || 'No research content available.';

                          // Debug: log first 200 chars to see what we're working with
                          console.log('Research content preview:', content.substring(0, 200));
                          console.log('Has newlines:', content.includes('\n'), 'Has \\n:', content.includes('\\n'));

                          // Convert markdown-style formatting to HTML
                          return content
                            // First, normalize line breaks (handle \n, \r\n, and multiple spaces before breaks)
                            .replace(/\s*\n\s*/g, '\n')
                            // Replace any sentence-ending punctuation followed by space and capital with line break
                            .replace(/([.!?])\s+(\*\*\d+\.|\*\*[A-Z])/g, '$1\n$2')
                            // Convert **Number. Title** to headings
                            .replace(/\*\*(\d+)\.\s+([^*]+)\*\*/g, '\n<h3 class="text-lg font-bold mt-6 mb-3">$1. $2</h3>\n')
                            // Convert remaining **bold** to <strong>
                            .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                            // Split into paragraphs by line breaks
                            .split('\n')
                            .map(para => para.trim())
                            .filter(para => para.length > 0)
                            .map(para => {
                              // Don't wrap if it's already an HTML tag
                              if (para.startsWith('<h')) return para;
                              return `<p class="mb-4 leading-7">${para}</p>`;
                            })
                            .join('\n');
                        })()
                      }}
                    />
                  </div>
                </div>
              </div>
              
              {selectedResearch.research.key_findings && (
                <div>
                  <Label>Key Findings</Label>
                  <div className="mt-2 space-y-2">
                    {Array.isArray(selectedResearch.research.key_findings) ?
                      selectedResearch.research.key_findings.map((finding: any, index: number) => (
                        <div key={index} className="text-sm bg-gray-50 dark:bg-slate-700 p-2 rounded">
                          {typeof finding === 'string' ? finding : (
                            <div className="space-y-1">
                              <div className="font-medium">{finding.finding}</div>
                              {finding.actionable && (
                                <div className="text-xs text-green-600 dark:text-green-400">Actionable: {finding.actionable}</div>
                              )}
                              {finding.importance && (
                                <div className="text-xs text-blue-600 dark:text-blue-400">Importance: {finding.importance}</div>
                              )}
                            </div>
                          )}
                        </div>
                      )) :
                      <pre className="text-sm bg-gray-50 dark:bg-slate-700 p-2 rounded">
                        {JSON.stringify(selectedResearch.research.key_findings, null, 2)}
                      </pre>
                    }
                  </div>
                </div>
              )}
              
              {selectedResearch.research.suggested_followup_questions && selectedResearch.research.suggested_followup_questions.length > 0 && (
                <div>
                  <Label>Suggested Follow-up Questions</Label>
                  <div className="mt-2 space-y-2">
                    {selectedResearch.research.suggested_followup_questions.map((question: string, index: number) => (
                      <div key={index} className="text-sm bg-blue-50 dark:bg-blue-950/30 p-2 rounded flex items-start gap-2">
                        <span className="text-blue-600 dark:text-blue-400 font-semibold">{index + 1}.</span>
                        <span>{question}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </ScrollArea>
          ) : null}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setSelectedResearch({ open: false, topicId: null, research: null })}
            >
              Close
            </Button>
            {selectedResearch.research && (
              <Button 
                variant="destructive"
                onClick={handleDeleteResearch}
                disabled={submitting}
              >
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Delete Research
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}